<template>
  <div class="licensing-wrapper">
    <h2 class="title ps-0">Licensing Verification</h2>

    <p class="description">
      To continue your registration as a Representative with NuEra Benefits, you
      must confirm whether you are a currently licensed insurance <br />
      broker/agent. State and federal regulations require us to verify your
      active license before granting access to the Broker-Rep Back Office.
    </p>

    <div class="radio-section">
        <p class="question">
          Are you a licensed insurance broker/agent?
          <span class="required">*</span>
        </p>
        <div class="radio-group">
          <label class="radio-option">
            <input type="radio" name="license_status" value="yes" v-model="licenseStatus" />
            <span class="custom-radio-outer"></span>
            <span class="label-text">
              Yes, I am a licensed insurance broker/agent
            </span>
          </label>

          <label class="radio-option">
            <input type="radio" name="license_status" value="no" v-model="licenseStatus" />
            <span class="custom-radio-outer"></span>
            <span class="label-text">No, I am not licensed</span>
          </label>
        </div>
    </div>
    <div class="" v-if="licenseStatus === 'yes'">
      <div class="form-card">
        <h4>Add License Upload Details</h4>

        <div class="form-grid">
          <div>
            <label class="label-title">State <span class="required-asterisk">*</span></label>
            <select v-model="state" :class="{ 'form-invalid': $v.state.$error }" class="custom-select">
              <option value="" disabled> Select State</option>
              <option v-for="(state, index) in stateLists" :value="state.value" :key="index">{{ state.text }}</option>
            </select>
            <div class="form-error text-left ml-3 mt-2" v-if="!$v.state.required && $v.state.$error">
              Field is required
            </div>
          </div>

          <div>
            <label>License Number <span class="required">*</span></label>
            <input type="text" v-model="licenseNumber" :class="{ 'form-invalid': $v.licenseNumber.$error }" @input="licenseNumber = licenseNumber.replace(/[^A-Za-z0-9]/g, '')" placeholder="Enter License Number"/>
            <div class="form-error text-left ml-3 mt-2" v-if="!$v.licenseNumber.required && $v.licenseNumber.$error">
              Field is required
            </div>
          </div>

          <div>
            <label>License Valid Until <span class="required">*</span></label>
            <input type="date" v-model="licenseValidUntil" :class="{ 'form-invalid': $v.licenseValidUntil.$error }" :min="minDate" />
            <div class="form-error text-left ml-3 mt-2"
              v-if="!$v.licenseValidUntil.required && $v.licenseValidUntil.$error">
              Field is required
            </div>
            <div class="form-error text-left ml-3 mt-2"
              v-if="!$v.licenseValidUntil.futureDate && $v.licenseValidUntil.$error">
              Date must be in the future
            </div>
          </div>

          <div>
            <label>Resident <span class="required">*</span></label>
            <select v-model="resident" :class="{ 'form-invalid': $v.resident.$error }" class="custom-select">
              <option value="" disabled>Select Resident Status</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            <div class="form-error text-left ml-3 mt-2" v-if="!$v.resident.required && $v.resident.$error">
              Field is required
            </div>
          </div>
        </div>

        <div class="upload-section">
          <p>
            Upload License File (Max Size: 5MB, Allowed: jpg, png, pdf)
          </p>

          <label class="upload-btn">
            Choose File
            <input type="file" ref="fileInput" hidden @change="handleFileUpload" accept=".jpg,.jpeg,.png,.pdf" />
          </label>
        </div>

        <!-- upload file -->
        <!-- <div class="upload-file mt-3 mb-3" v-if="selectedFile">
          <h6 class="mb-0">Upload License File</h6>
          <div class="">
            <img src="../../assets//images/screenshot.png" alt="" class="mt-2" />
          </div>
        </div> -->

        <div class="d-flex justify-content-end mb-4">
          <button class="button-submit" type="button" @click="uploadLicense">Upload</button>
        </div>

        <!-- Show selected filename -->
        <div class="selected-file-info mt-2 mb-3" v-if="selectedFile">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <p class="mb-0">
                <strong>Selected file:</strong> {{ selectedFile.name }}
                <span class="file-size">({{ formatFileSize(selectedFile.size) }})</span>
              </p>
            </div>
            <div>
              <a href="#" @click="previewSelectedFile" v-b-tooltip.hover title="Preview" class="me-2">
                <i class="fas fa-eye"></i> Preview
              </a>
            </div>
          </div>
        </div>
        <!-- uploaded table section -->
        <UploadedFileTable :licenses="licenses" @deleteLicense="deleteLicense" />
      </div>
      <b-form-checkbox v-model="certificationChecked" class="checkbox-default verify-checkbox mt-4">
        <p class="check-text">I certify that the license(s) uploaded are valid, active, and issued
          in my name.</p>
      </b-form-checkbox>
    </div>

    <Warning v-if="licenseStatus === 'yes'" />
    <ValidDisclaimer v-if="licenseStatus === 'no'" />

    <!-- File Preview Modal -->
    <b-modal id="file-preview-modal" v-model="showPreviewModal" size="lg" centered hide-header hide-footer
      class="file-preview-modal">
      <div class="preview-container">
        <div class="preview-header">
          <h5 class="mb-0">{{ previewFileName }}</h5>
          <button type="button" class="btn-close" @click="closePreviewModal" aria-label="Close"></button>
        </div>
        <div class="preview-body" v-if="previewUrl">
          <div v-if="isImageFile" class="image-preview">
            <img :src="previewUrl" :alt="previewFileName" class="img-fluid" />
          </div>
          <div v-else-if="isPdfFile" class="pdf-preview">
            <iframe :src="previewUrl" width="100%" height="500px" frameborder="0"></iframe>
          </div>
          <div v-else class="no-preview">
            <p>Preview not available for this file type.</p>
            <a :href="previewUrl" :download="previewFileName" class="btn btn-primary">Download File</a>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- action button -->
    <div class="action-btn split-action mt-3">
      <button class="button-back" @click="onBack" type="button">
        Back
      </button>
      <div class="action-next">
        <button class="button-save" @click.prevent="saveAndContinue" type="button" :disabled="!isNextButtonEnabled">
          Save & Continue Later
        </button>
        <button class="button-submit" :class="{ 'button-disable': !isNextButtonEnabled }" type="submit" @click="onNext"
          :disabled="!isNextButtonEnabled">Next</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { required } from "vuelidate/lib/validators";
import "../../assets/css/lead-listing.css";
import UploadedFileTable from "./UploadedFileTable.vue";
import ValidDisclaimer from "./ValidDisclaimer.vue";
import Warning from "./Warning.vue";
import axios from "@/utils/regAxios";
import ScreenShot from "@/utils/screenshot";
import Helper from "@/resource/Helper";

export default Vue.extend({
  name: "LicenseUpload",
  components: { Warning, ValidDisclaimer, UploadedFileTable },
  props: {
    initialLicenseStatus: {
      type: String,
      default: ""
    },
    form: {
      type: Object,
      default: () => ({})
    },
    fields: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      licenseStatus: this.initialLicenseStatus, // 'yes' | 'no'
      stateLists: [],
      state: '',
      licenseNumber: '',
      licenseValidUntil: '',
      resident: '',
      selectedFile: null,
      uploadProgress: 0,
      licenses: [],
      certificationChecked: false,
      showPreviewModal: false,
      previewUrl: null,
      previewFileName: '',
      currentFileType: ''
    };
  },
  validations: {
    state: { required },
    licenseNumber: {
      required
    },
    licenseValidUntil: {
      required,
      futureDate: value => {
        if (!value) return false;
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return selectedDate > today;
      }
    },
    resident: { required },
  },
  watch: {
    initialLicenseStatus(newVal) {
      this.licenseStatus = newVal;
    },
    licenseStatus(newVal) {
      // Reset certification checkbox when license status changes
      if (newVal === 'no') {
        this.certificationChecked = false;
      }
    },
    fields: {
      handler(newFields) {
        if (newFields && newFields.length > 0) {
          this.populateLicenseData(newFields);
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    isNextButtonEnabled() {
      return this.licenseStatus === 'yes' && this.certificationChecked && this.licenses.length > 0;
    },
    minDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today.toISOString().split('T')[0];
    },
    isImageFile() {
      return this.currentFileType && this.currentFileType.startsWith('image/');
    },
    isPdfFile() {
      return this.currentFileType === 'application/pdf';
    }
  },
  methods: {
    populateLicenseData(fields) {

      if (Array.isArray(fields) && fields.length > 0 && Array.isArray(fields[0])) {
        this.licenses = []; // Clear existing licenses

        fields.forEach((licenseFields) => {
          const license = {};
          licenseFields.forEach(field => {
            license[field.field_name] = field.value;
          });

          // Add license to the licenses array
          if (license.license_number != "" && license.license_state != "" && license.license_exp_date != "" && license.license_resident != "") {
            this.licenses.push({
              license_number: license.license_number,
              license_state: license.license_state,
              license_exp_date: license.license_exp_date,
              license_resident: parseInt(license.license_resident) || 0,
              license_doc: license.license_doc || 'No file',
              isExistingLicense: true // Flag to indicate this is from API
            });
          }
        });

        if (this.licenses.length > 0 && this.licenses[0].license_number !== "") {
          this.licenseStatus = 'yes';
          this.certificationChecked = true;
        }else{
          this.licenseStatus = 'no';
        }

      } else if (fields && fields.length > 0) {
        // Single license or other endpoint - use form object
        // This case handles when form is properly constructed
        console.log('Using form object approach');
      } else {
        console.log('No license data found in fields');
      }
    },
    async onNext() {
      if (this.licenseStatus === 'yes') {
        if (this.licenses.length === 0) {
          Helper.infoMessage(this, 'error', 'Please add at least one license');
          return;
        }

        try {
          await this.submitLicenseData();
          this.$emit("onSuccessLicenseInfo", this.licenseStatus);
        } catch (error) {
          console.error('Error submitting license data:', error);
          const responseData = error && error.response && error.response.data;
          const responseErrors = responseData && responseData.errors;
          if (responseErrors && typeof responseErrors === 'object') {
            Object.values(responseErrors).forEach(function(messages) {
              if (Array.isArray(messages)) {
                messages.forEach(function(msg) { Helper.infoMessage(this, 'error', msg); }, this);
              }
            }, this);
          } else {
            Helper.infoMessage(this, 'error', (responseData && responseData.message) || 'Error submitting license data. Please try again.');
          }
        }
      } else {
        this.$emit("onSuccessLicenseInfo", this.licenseStatus);
      }
    },
    async submitLicenseData() {
      const formData = new FormData();

      // Add registration_id from store
      const registrationId = this.$store.getters.regTempId;
      formData.append('registration_id', registrationId || '');

      // Add all licenses
      this.licenses.forEach((license, index) => {
        formData.append(`license_number[${index}]`, license.license_number);
        formData.append(`license_state[${index}]`, license.license_state);
        formData.append(`license_exp_date[${index}]`, license.license_exp_date);
        formData.append(`license_resident[${index}]`, license.license_resident);
        formData.append(`license_doc[${index}]`, license.license_doc);
        // Add flag to indicate if this is an update (existing) or insert (new)
        formData.append(`is_existing_license[${index}]`, license.isExistingLicense ? '1' : '0');
      });

      let loader = this.$loading.show();

      try {
        // Add screenshot like other components
        const screenshot = await ScreenShot.captureScreenShot(ScreenShot.regScreenShotSelector);
        if (screenshot) {
          formData.append('screenshot', screenshot);
        }

        const response = await axios.post('/v3/registration/license-info', formData);

        return response.data;
      } finally {
        loader.hide();
      }
    },
    onBack() {
      this.$emit("onBackLicenseInfo", this.licenseStatus);
    },
    async saveAndContinue() {
      if(!this.isNextButtonEnabled){
        return;
      }
      const formData = new FormData();
      const registrationId = this.$store.getters.regTempId;
      formData.append('registration_id', registrationId || '');
      formData.append('license_status', this.licenseStatus || '');
      formData.append('certification_checked', this.certificationChecked ? '1' : '0');
      formData.append('savecont', '1');
      this.licenses.forEach((license, index) => {
        formData.append(`license_number[${index}]`, license.license_number);
        formData.append(`license_state[${index}]`, license.license_state);
        formData.append(`license_exp_date[${index}]`, license.license_exp_date);
        formData.append(`license_resident[${index}]`, license.license_resident);
        // Only append license_doc as file for new licenses with actual File objects
        const isFile = license.license_doc && (license.license_doc instanceof File || (license.license_doc.name && license.license_doc.size));
        if (!license.isExistingLicense && isFile) {
          formData.append(`license_doc[${index}]`, license.license_doc, license.license_doc.name);
        } else if (license.isExistingLicense) {
          formData.append(`license_doc[${index}]`, license.license_doc || '');
        } else {
          formData.append(`license_doc[${index}]`, '');
        }
        formData.append(`is_existing_license[${index}]`, license.isExistingLicense ? '1' : '0');
      });

      const screenshot = await ScreenShot.captureScreenShot(ScreenShot.regScreenShotSelector);
        if (screenshot) {
          formData.append('screenshot', screenshot);
      }


      this.$emit("onOpenSaveAndContinue", formData);
    },
    getStateList() {
      let app = this
      let stateListObj = {}
      stateListObj = Helper.getStatesList()
      for (const key in stateListObj) {
        if (Object.prototype.hasOwnProperty.call(stateListObj, key)) {
          app.stateLists.push({ text: stateListObj[key], value: key });
        }
      }
    },
    handleFileUpload(event) {
      const target = event.target;
      if (target.files && target.files[0]) {
        const file = target.files[0];

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          Helper.infoMessage(this, 'error', 'File size must be less than 5MB');
          return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          Helper.infoMessage(this, 'error', 'Only JPG, PNG, and PDF files are allowed');
          return;
        }

        this.selectedFile = file;
        this.uploadFile();

        // Reset file input so same file can be selected again
        const fileInput = this.$refs.fileInput;
        if (fileInput) {
          fileInput.value = '';
        }
      }
    },
    uploadFile() {
      if (!this.selectedFile) return;

      // Simulate upload progress
      this.uploadProgress = 0;
      const interval = setInterval(() => {
        this.uploadProgress += 10;
        if (this.uploadProgress >= 100) {
          clearInterval(interval);
          this.uploadProgress = 100;
        }
      }, 200);
    },
    uploadLicense() {
      // Validate form fields
      this.$v.$touch();
      if (this.$v.$invalid || !this.selectedFile) {
        Helper.infoMessage(this, 'error', 'Please fill all required fields and select a file');
        return;
      }

      // Check for duplicate license number
      const isDuplicate = this.licenses.some(license => 
        license.license_number === this.licenseNumber.toUpperCase()
      );
      
      if (isDuplicate) {
        Helper.infoMessage(this, 'error', 'This license number already exists. Please enter a different license number.');
        return;
      }

      // Add license to array
      const license = {
        license_number: this.licenseNumber.toUpperCase(),
        license_state: this.state,
        license_exp_date: this.licenseValidUntil,
        license_resident: this.resident === 'yes' ? 1 : 0,
        license_doc: this.selectedFile,
        isExistingLicense: false // Flag to indicate this is a new upload
      };

      this.licenses.push(license);

      // Reset form
      this.resetLicenseForm();

      Helper.infoMessage(this, 'success', 'License added successfully!');
    },
    deleteLicense(index) {
      this.licenses.splice(index, 1);
    },
    resetLicenseForm() {
      this.state = '';
      this.licenseNumber = '';
      this.licenseValidUntil = '';
      this.resident = '';
      this.selectedFile = null;
      this.uploadProgress = 0;
      this.$v.$reset();
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    previewSelectedFile() {
      if (this.selectedFile && this.selectedFile instanceof File) {
        this.previewFileName = this.selectedFile.name;
        this.currentFileType = this.selectedFile.type;
        this.previewUrl = URL.createObjectURL(this.selectedFile);
        this.showPreviewModal = true;
      }
    },
    closePreviewModal() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
      this.showPreviewModal = false;
      this.previewFileName = '';
      this.currentFileType = '';
    }
  },
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
  mounted() {
    this.getStateList();
  }
});
</script>

<style scoped>
.f-12 {
  font-size: 12px;
}

.upload-file {
  background-color: #f3f7fa;
  padding: 1rem 1.5rem;
}

.progress-bar {
  background-color: #099d42;
  height: 8px;
  border-radius: 50px;
}

.progress {
  height: 8px;
  border-radius: 50px;
}

.button-disable {
  background-color: #9c9e9f;
}

.warning-disclaimer {
  border: 1px solid #eeb310;
  border-left: 6px solid #eeb310;
  padding: 1rem 0.5rem;
  border-radius: 6px;
}

.licensing-wrapper {
  max-width: 900px;
  margin: auto;
  padding: 24px;
  border-radius: 8px;
  font-family: Arial, sans-serif;
}

.title {
  text-align: center;
  color: var(--VUE_APP_REGISTRATION_START_BROKER_REP_COLOR);
  font-weight: 600;
}

.description {
  text-align: center;
  font-size: 14px;
  color: #222;
  margin-bottom: 24px;
  line-height: 30px;
}

.radio-section {
  margin-bottom: 24px;
  text-align: center;
}

.question {
  font-weight: 600;
  margin-bottom: 12px;
  text-align: center;
}

.form-container {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  padding: 20px;
  max-width: 800px;
}

.question {
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 15px;
  text-align: center;
}

.required {
  color: #d93025;
}

.radio-group {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
}

.radio-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  font-size: 1rem;
}

.radio-option input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.custom-radio-outer {
  height: 14px;
  width: 14px;
  background-color: #eee;
  border: 1px solid #ccc;
  border-radius: 50%;
  margin-right: 10px;
  display: inline-block;
  position: relative;
}

.custom-radio-outer:after {
  content: "";
  position: absolute;
  display: none;
  top: 2px;
  left: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #127d96;
}

.radio-option input:checked~.custom-radio-outer:after {
  display: block;
  background: #127d96;
}

.radio-option input:checked~.label-text {
  color: #127d96;
}
.label-text{
  text-align: left;
}

.radio-option input:checked~.custom-radio-outer {
  border-color: #127d96;
}

.form-card {
  background: #fff;
  border-radius: 8px;
  padding: 3.5rem 3rem 2rem 3rem;
  box-shadow: 0 0 0 1px #e5e5e5;
}

.form-card h4 {
  margin-bottom: 16px;
  font-weight: 600;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-grid label {
  font-size: 13px;
  margin-bottom: 6px;
  display: block;
}

.form-grid input {
  width: 100%;
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.upload-section {
  margin-top: 20px;
}

.upload-section p {
  font-size: 13px;
  margin-bottom: 10px;
}

.upload-btn {
  display: inline-block;
  background: #38bdf8;
  color: #fff;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.upload-btn:hover {
  background: #0ea5e9;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.file-preview-modal .modal-dialog {
  max-width: 90vw;
}

.preview-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.preview-header h5 {
  color: #333;
  font-weight: 600;
  margin: 0;
}

.preview-body {
  padding: 1rem;
  max-height: 80vh;
  overflow: auto;
}

.image-preview {
  text-align: center;
}

.image-preview img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.pdf-preview {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.no-preview {
  text-align: center;
  padding: 3rem 2rem;
  color: #666;
}

.no-preview p {
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}
</style>
